/**
 * Disconnect Classifier
 *
 * IMPORTANT: This file is duplicated in apps/marketing/src/features/quickstart/utils/disconnectClassifier.ts.
 * Both copies must stay in sync. If you change one, change the other.
 *
 * Classifies a player-side disconnect reason as retryable (transient /
 * network blip — try to reconnect) or non-retryable (intentional / permanent
 * — give up and exit to the CTA).
 *
 * Used by `QuickstartCoordinator.onPlayerDisconnect` to decide whether to
 * call `attemptReconnect()`.
 *
 * Design rules:
 *
 * 1. **Default = non-retryable.** If we don't recognise the reason it's
 *    almost certainly because the host intentionally closed the session
 *    (game exit, all-players-left, host app shutdown, campaign cleared, ...).
 *    Retrying just produces dead signaling traffic targeted at a peer that
 *    no longer exists, drives the player to a "Reconnecting..." UI for no
 *    reason, and adds noise to the realtime API and Sentry.
 *
 * 2. **Host-initiated reasons → non-retryable.** Going forward the host
 *    always emits a `host:` prefix on reasons it sends down `peer.close()`.
 *    For backwards compatibility with hosts on older builds we also accept
 *    a fixed set of legacy unprefixed reasons.
 *
 * 3. **Ad-mode terminations → non-retryable.** Reasons prefixed `ad_mode:`
 *    indicate the game ended naturally (player died, level finished, etc.).
 *
 * 4. **Only WebRTC / network errors are retryable.** Specifically: ICE
 *    failures, transport drops, connection-lost, network errors and
 *    timeouts that aren't tagged as host-initiated.
 */

/**
 * Legacy host-initiated reasons that older host builds still emit WITHOUT
 * the `host:` prefix. Each entry has been since prefixed at the source —
 * this set is purely for backwards compatibility with hosts that haven't
 * been updated to a build containing the prefix.
 *
 * Removal criterion: an entry can be deleted once the corresponding host
 * emit-site has been on the `host:` prefix in production for at least one
 * full upgrade cycle (i.e. the minimum host build version currently allowed
 * to connect is greater than or equal to the version that shipped the
 * prefix). Don't sunset entries individually — track minimum-host-version
 * centrally and prune in batches.
 */
const LEGACY_HOST_INITIATED_REASONS: ReadonlySet<string> = new Set([
  'game_closed',
  'peer_connection_timeout',
  'duplicate_peer',
  'host_app_closed',
  'all_players_left_restart',
  'all_players_left_no_restart',
  'campaign_clear:active_match',
  'pavilion:no_players_timeout',
  'init:timeout',
  'invitation:invalidated',
  'ipc:stopstream',
  'coordinator_requested',
]);

/**
 * Returns `true` if the player should attempt a coordinator-level reconnect
 * after a peer disconnect, `false` if the player should give up and exit to
 * the CTA. Default is `false` for any unknown reason — reaching this code
 * path with an unrecognised reason almost always means the host went away
 * intentionally, and reconnecting to a dead peer just produces noise.
 */
export const isDisconnectRetryable = (kickReason?: string): boolean => {
  // No reason at all almost always means the host went away (refresh,
  // crash, intentional close). Don't reconnect — the peer-level retry
  // inside the WebRTC layer already handled transient drops.
  if (!kickReason) return false;

  const reason = kickReason.toLowerCase();

  // --- Non-retryable: host-initiated session end --------------------------

  // Future canonical form. Every host-initiated peer.close() should use this.
  if (reason.startsWith('host:')) return false;

  // Backwards-compat: hosts on older builds that emit unprefixed reasons.
  if (LEGACY_HOST_INITIATED_REASONS.has(reason)) return false;

  // Ad-mode session ending naturally (player died, game ended, etc.).
  if (reason.includes('ad_mode:')) return false;

  // --- Non-retryable: intentional disconnects from the player side --------

  if (reason.includes('quickstart:')) return false;
  if (reason.includes('player:close')) return false;

  // --- Non-retryable: explicit kicks / refusals / invalid sessions --------

  if (reason.includes('kicked')) return false;
  if (reason.includes('refused')) return false;
  if (reason.includes('invalid')) return false;

  // --- Retryable: transient WebRTC / network problems ---------------------

  if (reason.includes('transport_disconnected')) return true;
  if (
    reason.includes('ice_disconnected') ||
    reason.includes('ice_failed') ||
    reason.includes('ice_timeout') ||
    reason.startsWith('ice_') ||
    reason === 'ice'
  ) {
    return true;
  }
  if (reason.includes('network')) return true;
  if (reason.includes('timeout')) return true;
  if (reason.includes('connection_lost')) return true;

  // Default: unknown reason. Treat as host-initiated and don't reconnect.
  // This is the conservative choice — see file-level rule (1).
  return false;
};
